import { ChangeDetectionStrategy, Component, effect, inject, OnInit } from '@angular/core';
import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import {
  TUI_SPIN_ICONS,
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiHint,
  TuiIcon,
  TuiLabel, TuiLink,
  TuiTextfield
} from '@taiga-ui/core';
import { TuiChevron, TuiTextarea } from '@taiga-ui/kit';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { tuiPure } from '@taiga-ui/cdk';
import { TuiDialogService } from '@taiga-ui/experimental';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { AuthModal } from '../../pattern/auth-modal/auth-modal';
import { AuthStore } from '../../core/auth/auth.store';
import { InfoModal } from '../../pattern/info-modal/info-modal';
import { User } from '../../core/auth/auth.model';
import { PasswordModal } from '../../pattern/password-modal/password-modal';
import { SessionFacade } from '../../core/auth/facades/session.facade';
import { MainLayoutFacade } from './main-layout.facade';
import { MainLayoutStore } from './main-layout.store';
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { SegmentModal } from '../../pattern/segment-modal/segment-modal';
import { AiQuestionUi } from '../../core/ai/ai.model';
import dayjs from 'dayjs';
import { UserFacade } from '../../core/auth/facades/user.facade';
import { MarkdownService } from '../../core/markdown/markdown.service';
import { AudienceForm } from './audience-form/audience-form';
import { Audience } from './audience-form/audience-form.model';
import { FaqQuestions } from './faq-questions/faq-questions';
import { AiQuestions } from './ai-questions/ai-questions';
import { PLATFORMS } from './main-layout.const';

@Component({
  selector: 'app-main-layout',
  imports: [
    NgOptimizedImage,
    TuiButton,
    TuiLabel,
    TuiTextfield,
    ReactiveFormsModule,
    TuiIcon,
    TuiAppearance,
    TuiDataList,
    TuiDropdown,
    TuiChevron,
    TuiTextarea,
    TuiHint,
    AudienceForm,
    FaqQuestions,
    AiQuestions,
    AsyncPipe,
    TuiLink
  ],
  templateUrl: './main-layout.html',
  styleUrl: './main-layout.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: TUI_SPIN_ICONS,
      useValue: {
        increment: 'arrow-right',
        decrement: 'arrow-left'
      }
    },
    MainLayoutStore,
    MainLayoutFacade
  ]
})
export class MainLayout implements OnInit {
  readonly #dialogs = inject(TuiDialogService);
  protected readonly authStore = inject(AuthStore);
  readonly #sessionFacade = inject(SessionFacade);
  protected readonly mainLayoutStore = inject(MainLayoutStore);
  readonly #mainLayoutFacade = inject(MainLayoutFacade);
  readonly #fb = inject(FormBuilder);
  readonly #alertService = inject(TuiAlertService);
  readonly #userFacade = inject(UserFacade);
  readonly #markDownService = inject(MarkdownService);

  protected readonly audienceForm = this.#fb.group<Audience>({
    sector: this.#fb.control(null, [Validators.required]),
    segments: this.#fb.control(null, [Validators.required]),
    platform: this.#fb.nonNullable.control('', [Validators.required]),
    comment: this.#fb.nonNullable.control('', [Validators.required])
  });

  protected readonly sectors$ = this.#mainLayoutFacade.sectors$;
  protected readonly segments$ = this.audienceForm.controls.sector.valueChanges.pipe(
    filter((sector) => sector != null),
    switchMap((sector) => this.#mainLayoutFacade.getSegments(sector.id))
  );
  protected readonly platforms = PLATFORMS;

  protected isHeaderMenuOpen = false;
  protected faqItems = this.#mainLayoutFacade.faqQuestions;
  protected aiItems = this.#mainLayoutFacade.aiQuestions;
  protected isAskAiDisabled = this.#mainLayoutFacade.isAskAiDisabled;
  protected lastFaqQuestions = this.#mainLayoutFacade.lastFaqQuestions;
  protected selectedQuestion = new FormControl<string>('', {
    nonNullable: true
  });
  readonly #hasSector = toSignal(this.audienceForm.controls.sector.valueChanges, {
    initialValue: this.audienceForm.controls.sector.value
  });

  constructor() {
    effect(() => {
      const segments = this.audienceForm.controls.segments;

      if (this.#hasSector()) {
        segments.enable();
      } else {
        segments.disable();
      }
    });

    effect(() => {
      if (this.mainLayoutStore.activeItemIndex() === 0) {
        this.selectedQuestion.reset('');
      }
    });

    let processingAiQuestionsIdsSet: Set<number> | null;

    effect(() => {
      const aiQuestions = this.aiItems.value()?.list;
      let timeoutId: number;

      if (!aiQuestions?.length) {
        return;
      }

      const processingAiQuestionsIds = aiQuestions
        .filter((aiQuestion) => aiQuestion.status === 'processing' || aiQuestion.status === 'new')
        .map((item) => item.id);

      if (!processingAiQuestionsIdsSet) {
        processingAiQuestionsIdsSet = new Set(processingAiQuestionsIds);
      } else {
        for (const id of processingAiQuestionsIds) {
          if (!processingAiQuestionsIdsSet.has(id)) {
            processingAiQuestionsIdsSet.add(id);
          }
        }
      }

      if (processingAiQuestionsIdsSet.size === 0) {
        return;
      }

      for (const aiQuestion of aiQuestions) {
        if (processingAiQuestionsIdsSet.has(aiQuestion.id) && aiQuestion.status === 'completed') {
          aiQuestion.isAnimated.set(true);
          aiQuestion.isExpanded.set(true);

          processingAiQuestionsIdsSet.delete(aiQuestion.id);
        }
      }

      if (processingAiQuestionsIdsSet.size !== 0) {
        timeoutId = setTimeout(() => {
          this.aiItems.reload();
        }, 5000);
      }

      return () => clearInterval(timeoutId);
    });

    effect(() => {
      const isAiShown = this.authStore.isAiShown();

      if (!isAiShown) {
        this.mainLayoutStore.updateActiveItemIndex(0);
      }
    });

    effect((onCleanup) => {
      const user = this.authStore.user();
      let timeoutId: number;

      if (user) {
        if (user.approved) {
          return;
        }

        const deadline = dayjs(user.created_at).add(15, 'minute');
        const currentTime = dayjs();
        const isAiTimeLimitExceeded = currentTime.isAfter(deadline);

        if (isAiTimeLimitExceeded) {
          this.authStore.updateIsAiTimeLimitExceeded(true);
        } else {
          const timeout = deadline.diff(currentTime, 'milliseconds');
          timeoutId = setTimeout(() => this.authStore.updateIsAiTimeLimitExceeded(true), timeout);
          onCleanup(() => clearTimeout(timeoutId));
        }
      }
    });

    effect(() => {
      const user = this.authStore.user();

      if (!user) return;

      if (this.authStore.isAiTimeLimitExceeded() && !user.data?.['is_ai_time_limit_exceeded']) {
        this.#userFacade
          .changeUserData({
            is_ai_time_limit_exceeded: true
          })
          .subscribe(() => {
            this.#alertService
              .open('Время использования ai режима истекло', { label: 'Доступ закрыт!' })
              .subscribe();
          });
      }
    });
  }

  @tuiPure
  protected renderMarkdown(text: string) {
    return this.#markDownService.render(text);
  }

  protected toggleAiQuestion(item: AiQuestionUi) {
    item.isExpanded.set(!item.isExpanded());
  }

  protected logout() {
    this.#sessionFacade.logout();
  }

  protected openAuthModal() {
    this.#dialogs
      .open<User | string>(new PolymorpheusComponent(AuthModal), {
        label: 'Введите ваш Email',
        size: 's'
      })
      .subscribe((user) => {
        if (typeof user === 'string') {
          this.#dialogs
            .open(new PolymorpheusComponent(PasswordModal), {
              label: 'Введите код',
              size: 's',
              data: user
            })
            .subscribe();
        } else {
          this.#dialogs
            .open(new PolymorpheusComponent(InfoModal), {
              label: 'Временный доступ на 15 минут',
              size: 's',
              data: `Все новые аккаунты проходят обязательную
                 верификацию. После успешной проверки вы
                 получите уведомление на указанный email.

                 Сразу после регистрации вам предоставляется
                 временный доступ к личному кабинету на 15 минут.
                 `
            })
            .subscribe();
        }
      });
  }

  protected buildAudience() {
    if (!this.audienceForm.valid) return;

    this.#mainLayoutFacade.buildAudience(this.audienceForm).subscribe(() => {
      this.#dialogs
        .open(new PolymorpheusComponent(InfoModal), {
          label: 'Спасибо за обращение!',
          size: 's',
          data: 'Наш менеджер скоро свяжется с вами.'
        })
        .subscribe();

      this.audienceForm.reset();
    });
  }

  protected openSegmentModal() {
    this.#dialogs
      .open(new PolymorpheusComponent(SegmentModal), {
        label: 'Не нашел подходящий сегмент',
        size: 's',
        appearance: 'taiga ai-modal',
        data: {
          platforms: this.platforms
        }
      })
      .subscribe(() => {
        this.#dialogs
          .open(new PolymorpheusComponent(InfoModal), {
            label: 'Спасибо за обращение!',
            size: 's',
            data: 'Наш менеджер скоро свяжется с вами.'
          })
          .subscribe();

        this.audienceForm.reset();
      });
  }

  protected askAi() {
    this.#mainLayoutFacade.askAi(this.mainLayoutStore.query()).subscribe();
  }

  protected onTypingCompleted(question: AiQuestionUi) {
    question.isAnimated.set(false);
  }

  ngOnInit(): void {
    this.selectedQuestion.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => {
        this.mainLayoutStore.updateQuery(value);
        this.mainLayoutStore.updateCurrentPage(1);
      });
  }
}
